import { reloadIfStaleChunk } from '../../lib/version';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { BiicLockup } from '../brand/BiicLogo';
import { useAuthStore } from '../../lib/auth/AuthContext';
import {
  Activity, Camera, Map, Eye, Search, HelpCircle, LogOut,
  Bell, ShieldCheck, FileText, Settings, BarChart2, MoreHorizontal,
  Network, CalendarDays, Key,
  Landmark, Radar, Flame, Rocket, ScanSearch, ClipboardCheck, BadgeCheck,
} from 'lucide-react';
import { useState, Suspense, Component } from 'react';
import type { ReactNode } from 'react';
import { useBreakpoint } from '../../lib/hooks/useBreakpoint';
import { orderNavItems } from '@shared/constants/platform';

// ─────────────────────────────────────────────
// Error boundary — catches silent render errors
// ─────────────────────────────────────────────

interface EBState { error: Error | null }
class PageErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e }; }
  componentDidCatch(e: Error) { reloadIfStaleChunk(e); }
  render() {
    if (this.state.error) {
      if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(this.state.error.message)) {
        return <div style={{ padding: '48px 24px', textAlign: 'center', fontSize: 13, color: '#4d5a52' }}>A new version of BIIC is available. Loading it…</div>;
      }
      return (
        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#B12E28', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
            Page error
          </div>
          <div style={{ fontSize: 12, color: '#4d5a52', maxWidth: 480, margin: '0 auto' }}>
            {this.state.error.message}
          </div>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{ marginTop: 16, padding: '9px 20px', borderRadius: 8, background: '#0d3318', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, letterSpacing: '.01em', fontFamily: 'inherit' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────
// Portal definitions — one per role group
// ─────────────────────────────────────────────

type NavItem = { path: string; label: string };

const REPORTER_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/capture',   label: 'Field verification' },
  { path: '/support',   label: 'Support' },
];

// Investigator / Association Analyst — absorbs the former analyst review queue
// and case triage workspace alongside field capture duties.
const FIELD_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/capture',   label: 'Field verification' },
  { path: '/review',    label: 'Quality assurance' },
  { path: '/worklist',  label: 'CMS' },
  { path: '/audit',     label: 'Management review' },
  { path: '/legal',     label: 'Legal library' },
  { path: '/history',   label: 'History' },
  { path: '/analytics', label: 'Analytics' },
  { path: '/reports',   label: 'Reports' },
  { path: '/support',   label: 'Support' },
];

// Executive (Association Executive). Industry command centre and case
// outcomes; doesn't manage a brand portfolio — so no Registry / Alerts /
// API keys entries here.
// Patterns + Quarterly Report intentionally hidden until the underlying
// queries are cross-shard-safe and the data set is meaningful (the routes
// stay registered so old links keep working, just nav entries are gone).
const EXEC_NAV: NavItem[] = [
  { path: '/worklist',           label: 'CMS' },
  { path: '/audit',              label: 'Management review' },
  { path: '/legal',              label: 'Legal library' },
  { path: '/dashboard',          label: 'Dashboard' },
  { path: '/reports',            label: 'Reports' },
  { path: '/support',            label: 'Support' },
];

// Compliance (Manufacturer Brand Protection Lead). Portfolio-scoped
// dashboard, counterfeit alerting, API for enterprise integration.
const GOVERNANCE_NAV: NavItem[] = [
  { path: '/worklist',           label: 'CMS' },
  { path: '/audit',              label: 'Management review' },
  { path: '/legal',              label: 'Legal library' },
  { path: '/dashboard',          label: 'Dashboard' },
  { path: '/reports',            label: 'Reports' },
  { path: '/support',            label: 'Support' },
  { path: '/registry',           label: 'Registry' },
  { path: '/alerts',             label: 'Alerts' },
  { path: '/settings/api-keys',  label: 'API Keys' },
];

// SARS revenue liaison. Read-only trade intelligence — deliberately
// nothing else. Not a smaller version of the admin console; a
// completely different, much narrower portal by design (sars_official
// never sees case management or capture review).
const SARS_NAV: NavItem[] = [
  { path: '/intel',   label: 'Operational intelligence' },
  { path: '/support', label: 'Support' },
];

const ADMIN_NAV: NavItem[] = [
  { path: '/dashboard',   label: 'Dashboard' },
  { path: '/capture',     label: 'Field verification' },
  { path: '/history',     label: 'History' },
  { path: '/review',      label: 'Quality assurance' },
  { path: '/brand',       label: 'Brand portal' },
  { path: '/worklist',    label: 'CMS' },
  { path: '/audit',       label: 'Management review' },
  { path: '/legal',       label: 'Legal library' },
  { path: '/analytics',   label: 'Analytics' },
  { path: '/registry',    label: 'Registry' },
  { path: '/reports',     label: 'Reports' },
  { path: '/admin',       label: 'Admin' },
  { path: '/support',     label: 'Support' },
];

const ICON_MAP: Record<string, React.ElementType> = {
  '/dashboard':          Activity,
  '/intel':              Network,
  '/intel/excise':       Landmark,
  '/biic':               Radar,
  '/biic/leads':         Flame,
  '/biic/scans':         ScanSearch,
  '/onboarding':         Rocket,
  '/capture':            Camera,
  '/worklist':           Map,
  '/audit':              ClipboardCheck,
  '/notifications':      Bell,
  '/review':             Eye,
  '/brand':              BadgeCheck,
  '/analytics':          BarChart2,
  '/analytics/patterns': Network,
  '/history':            FileText,
  '/registry':           Search,
  '/reports':            FileText,
  '/reports/quarterly':  CalendarDays,
  '/alerts':             Bell,
  '/settings/api-keys':  Key,
  '/admin':              ShieldCheck,
  '/support':            HelpCircle,
};

function navForRole(role: string): NavItem[] {
  if (role === 'admin') return ADMIN_NAV;
  if (role === 'exec') return EXEC_NAV;
  if (role === 'governance') return GOVERNANCE_NAV;
  if (role === 'reporter') return REPORTER_NAV;
  if (role === 'sars_official') return SARS_NAV;
  return FIELD_NAV;
}

function portalLabel(role: string): string {
  if (role === 'admin') return 'Admin';
  if (role === 'exec') return 'Executive';
  if (role === 'governance') return 'Compliance';
  if (role === 'investigator') return 'Investigator';
  if (role === 'biic_investigator') return 'Investigator';
  if (role === 'reporter') return 'Reporter';
  if (role === 'sars_official') return 'SARS';
  return 'Field';
}

// Modules an admin can always reach for a demo, even when their workspace
// profile leaves the pill out. Only shown when the tenant has that service on.
const ADMIN_EXTRA_NAV: { module: string; item: NavItem }[] = [
  { module: 'intel', item: { path: '/intel', label: 'Operational intelligence' } },
  { module: 'biic',  item: { path: '/biic',  label: 'Trade Intelligence' } },
];

// Pages that work on large imagery use the full screen width instead of the 1480px column.
const FULL_WIDTH = [/^\/captures\/[^/]+$/];

/** Bottom-bar labels: short enough to read at a glance on a phone (full names stay on desktop). */
const SHORT_LABELS: Record<string, string> = {
  'Trade Intelligence': 'Trade', 'Operational intelligence': 'Ops map', 'Quality assurance': 'QA',
  'Management review': 'Review', 'Field verification': 'Scan', 'Case management system': 'Cases', CMS: 'Cases',
  'Legal library': 'Legal', 'Performance report': 'Report', Notifications: 'Alerts',
};
const shortLabel = (label: string) => SHORT_LABELS[label] ?? (label.length > 10 ? label.split(' ')[0] : label);

export function AppShell() {
  const { user, logout } = useAuthStore();
  const workspace = useAuthStore(s => s.workspace);
  const platformModules = useAuthStore(s => s.platformModules);
  const tenantOverride = useAuthStore(s => s.tenantOverride);
  const location = useLocation();
  const isMobile = useBreakpoint(768);
  const role = user?.role ?? 'reporter';
  const [moreOpen, setMoreOpen] = useState(false);

  // The composed workspace wins when the user has finished onboarding;
  // otherwise fall back to the legacy role nav so nothing ever goes blank.
  const workspaceActive = workspace?.status === 'active' && (workspace.nav?.length ?? 0) > 0;
  const rawNavItems: NavItem[] = (() => {
    if (!workspaceActive || !workspace) return navForRole(role);
    const items = [...workspace.nav];
    const seen = new Set(items.map(i => i.path));
    if (role === 'admin') {
      for (const extra of ADMIN_EXTRA_NAV) {
        if (platformModules.includes(extra.module as never) && !seen.has(extra.item.path)) {
          items.push(extra.item);
          seen.add(extra.item.path);
        }
      }
      if (!seen.has('/brand')) { items.push({ path: '/brand', label: 'Brand portal' }); seen.add('/brand'); }
      if (!seen.has('/admin')) items.push({ path: '/admin', label: 'Admin' });
    }
    return items;
  })();
  // Canonical main-nav order (shared with the API's composeNav): Trade intelligence,
  // Operational intelligence, Field verification, Quality assurance, CMS, Management
  // review, then everything else in its listed order, Support last.
  const navItems: NavItem[] = orderNavItems(rawNavItems);
  const portal = (workspaceActive && workspace?.eyebrow) ? workspace.eyebrow : portalLabel(role);
  const needsWorkspace = !!workspace && workspace.status !== 'active';

  // For each nav item, find the LONGEST item.path that's a prefix of the
  // current pathname. Only that one is active. Without this, visiting
  // /analytics/patterns lit BOTH "Analytics" and "Patterns" because
  // startsWith('/analytics') matches both.
  const activePath = (() => {
    if (location.pathname === '/' || location.pathname === '/dashboard') return '/dashboard';
    const matches = navItems
      .map(n => n.path)
      .filter(p => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (matches.length === 0) return null;
    return matches.reduce((best, p) => p.length > best.length ? p : best);
  })();
  function isActive(path: string) {
    return activePath === path;
  }

  const initials = user?.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  // Bottom nav: place /capture in the center slot.
  // When overflow exists, use only 1 right-side item so total visible = 2+camera+1+More = 5
  // slots, keeping camera at true center (50%). Without overflow, 2+camera+2 = 5 items ✓.
  // Centre button: Capture when the user has Enforce.
  const captureItem = navItems.find(n => n.path === '/capture');
  let bottomPrimary: NavItem[];
  let bottomOverflow: NavItem[];
  if (captureItem) {
    // Layout policy: Camera in the middle, Support pinned to the far right
    // when present. Everything else fills the left side; anything that
    // doesn't fit spills into the "More" overflow menu.
    const rest = navItems.filter(n => n.path !== captureItem.path);
    const supportItem = rest.find(n => n.path === '/support') ?? null;
    const others = supportItem ? rest.filter(n => n.path !== '/support') : rest;

    if (supportItem) {
      // Reserve right slot for support. Left side gets up to 2 items;
      // the rest spill into "More".
      const left = others.slice(0, 2);
      bottomOverflow = others.slice(2);
      bottomPrimary = [...left, captureItem, supportItem];
    } else {
      // No support to pin — use the old balanced layout.
      const wouldOverflow = others.length > 4;
      const rightCount = wouldOverflow ? 1 : 2;
      const left  = others.slice(0, 2);
      const right = others.slice(2, 2 + rightCount);
      bottomOverflow = others.slice(2 + rightCount);
      bottomPrimary  = [...left, captureItem, ...right];
    }
  } else {
    bottomPrimary  = navItems.slice(0, 4);
    bottomOverflow = navItems.slice(4);
  }

  return (
    <div className={`knv-shell${FULL_WIDTH.some(r => r.test(location.pathname)) ? ' knv-shell-full' : ''}`}>

      {/* ── Main ── */}
      <main className="knv-main">
        {/* Top nav — hidden on mobile via CSS class knv-top-nav */}
        <div className="knv-top-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'nowrap' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 auto' }}>
            <Link to="/" title="BIIC" aria-label="BIIC home" style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 18, textDecoration: 'none', flex: 'none' }}>
              <BiicLockup size={34} caption={null} />
            </Link>
            <span style={{
              fontFamily: "'Jost', sans-serif", fontWeight: 600, fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase',
              color: '#DE3831', background: 'rgba(222,56,49,.08)', borderRadius: 6, padding: '5px 11px', marginRight: 6, flex: 'none',
            }}>
              {portal}
            </span>
            {navItems.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`knv-nav-pill${isActive(path) ? ' active' : ''}`}
              >
                {label}
              </Link>
            ))}
            {needsWorkspace && (
              <Link
                to="/onboarding"
                className="knv-nav-pill"
                style={{ background: 'rgba(222,56,49,.08)', color: '#B12E28' }}
              >
                Set up workspace
              </Link>
            )}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <Link
              to="/notifications"
              aria-label="Notifications"
              title="Notifications — allocations, court dates, approvals"
              style={{ width: 44, height: 44, borderRadius: '50%', background: isActive('/notifications') ? '#0d3318' : '#e4e9e3', display: 'grid', placeItems: 'center', border: 'none', cursor: 'pointer', color: isActive('/notifications') ? '#fff' : '#1f2a24', position: 'relative', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', textDecoration: 'none' }}
            >
              <Bell size={18} />
              <span style={{ position: 'absolute', top: 9, right: 9, width: 8, height: 8, borderRadius: '50%', background: '#DE3831' }} className="pulse-dot" />
            </Link>

            <Link
              to="/settings/security"
              title="Security settings"
              style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5a3c, #5a3824)', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 500, fontSize: 13, border: '2px solid #fff', textDecoration: 'none', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              aria-label="Settings"
            >
              {isActive('/settings') ? <Settings size={16} /> : initials}
            </Link>

            <button
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              style={{ width: 44, height: 44, borderRadius: '50%', background: '#e4e9e3', display: 'grid', placeItems: 'center', border: 'none', cursor: 'pointer', color: '#1f2a24', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {tenantOverride && (
          <div style={{
            background: '#0d3318', color: '#fff', borderRadius: 8, padding: '9px 16px',
            fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <span style={{ fontFamily: "'Jost', sans-serif", fontWeight: 600, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FFD166' }}>
              Acting as tenant
            </span>
            <span style={{ opacity: 0.9 }}>Every request is scoped to {tenantOverride} and written to the audit log.</span>
            <Link to="/admin/tenants" style={{ color: '#fff', marginLeft: 'auto', textDecoration: 'underline' }}>Manage</Link>
          </div>
        )}
        {/* Mobile header (only on mobile) */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BiicLockup size={30} caption={null} />
              <span style={{
                padding: '4px 9px', borderRadius: 6, fontSize: 9.5, fontWeight: 600,
                background: 'rgba(222,56,49,.08)', color: '#DE3831',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                fontFamily: "'Jost', sans-serif",
              }}>{portal}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link
                to="/notifications"
                aria-label="Notifications"
                style={{ width: 44, height: 44, borderRadius: '50%', background: '#e4e9e3', display: 'grid', placeItems: 'center', border: 'none', cursor: 'pointer', color: '#1f2a24', position: 'relative', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', textDecoration: 'none' }}
              >
                <Bell size={16} />
                <span style={{ position: 'absolute', top: 8, right: 8, width: 7, height: 7, borderRadius: '50%', background: '#DE3831' }} className="pulse-dot" />
              </Link>
              <Link
                to="/settings/security"
                style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5a3c, #5a3824)', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 500, fontSize: 13, border: '2px solid #fff', textDecoration: 'none', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                aria-label="Settings"
              >
                {initials}
              </Link>
            </div>
          </div>
        )}

        <PageErrorBoundary>
          <Suspense fallback={
            <div style={{ padding: '64px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid #e4e9e3', borderTopColor: '#0d3318', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 12.5, color: '#66736b', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>Loading…</span>
            </div>
          }>
            <Outlet />
          </Suspense>
        </PageErrorBoundary>
      </main>

      {/* ── Mobile bottom nav ── */}
      <nav className="knv-bottom-nav" aria-label="Main navigation">
        {bottomPrimary.map(({ path, label }) => {
          const Icon = ICON_MAP[path] ?? Activity;
          const isCapture = !!captureItem && path === captureItem.path;
          if (isCapture) {
            return (
              <Link key={path} to={path} className={`capture-center${isActive(path) ? ' active' : ''}`} aria-label="Capture">
                <div className="capture-center-btn">
                  <Camera size={26} />
                </div>
              </Link>
            );
          }
          return (
            <Link key={path} to={path} className={isActive(path) ? 'active' : ''} aria-label={label}>
              <span className="knv-bn-icon"><Icon size={22} strokeWidth={isActive(path) ? 2.3 : 2} /></span>
              <span className="knv-bn-label">{shortLabel(label)}</span>
            </Link>
          );
        })}
        {bottomOverflow.length > 0 && (
          <button
            className={moreOpen ? 'active' : ''}
            onClick={() => setMoreOpen(o => !o)}
            aria-label="More navigation"
          >
            <span className="knv-bn-icon"><MoreHorizontal size={22} /></span>
            <span className="knv-bn-label">More</span>
          </button>
        )}
      </nav>

      {/* ── Mobile overflow menu ── */}
      {moreOpen && isMobile && (
        <>
          <div className="knv-drawer-overlay" onClick={() => setMoreOpen(false)} />
          <div className="knv-drawer" style={{ padding: '10px 0 16px' }}>
            <div aria-hidden style={{ paddingBottom: 12 }}><div style={{ width: 44, height: 5, borderRadius: 999, background: '#d3dbd2', margin: '0 auto' }} /></div>
            <div style={{ padding: '0 20px 12px', fontSize: 12, color: '#4d5a52', fontFamily: "'Jost', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>More</div>
            <div className="knv-more-grid">
              {bottomOverflow.map(({ path, label }) => {
                const Icon = ICON_MAP[path] ?? Activity;
                return (
                  <Link key={path} to={path} onClick={() => setMoreOpen(false)} className={`knv-more-tile${isActive(path) ? ' active' : ''}`}>
                    <Icon size={24} />
                    {label}
                  </Link>
                );
              })}
            </div>
            <button
              onClick={logout}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 20px', width: '100%',
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#66736b', fontSize: 15, fontFamily: 'inherit',
              }}
            >
              <LogOut size={18} />
              Log out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
